/**
 * UploadManager - Handles file upload via drag-and-drop and file input
 */
export class UploadManager {
  private uploadZone: HTMLElement | null = null;
  private fileInput: HTMLInputElement | null = null;
  private onFileUpload: ((file: File) => void) | null = null;
  private initialized: boolean = false;
  private clickHandler: ((e: Event) => void) | null = null;
  private lastClickTime: number = 0;

  init(onFileUpload: (file: File) => void): void {
    console.log('[UploadManager] Initializing upload manager...');
    
    if (this.initialized) {
      console.warn('[UploadManager] Already initialized, skipping');
      return;
    }
    
    this.onFileUpload = onFileUpload;
    
    this.uploadZone = document.getElementById('imageUploadZone');
    this.fileInput = document.getElementById('fileInput') as HTMLInputElement;

    console.log('[UploadManager] Upload zone:', this.uploadZone ? 'FOUND' : 'NOT FOUND');
    console.log('[UploadManager] File input:', this.fileInput ? 'FOUND' : 'NOT FOUND');

    if (!this.uploadZone || !this.fileInput) {
      console.error('[UploadManager] Required elements not found - cannot initialize');
      return;
    }

    this.setupEventListeners();
    this.initialized = true;
    console.log('[UploadManager] Event listeners set up successfully');
  }

  private setupEventListeners(): void {
    console.log('[UploadManager] Setting up event listeners...');
    if (!this.uploadZone || !this.fileInput) {
      console.error('[UploadManager] Cannot setup listeners - elements are null');
      return;
    }

    // Click to upload - but only for the placeholder, not the whole upload zone
    console.log('[UploadManager] Adding click handler to upload placeholder');
    const uploadPlaceholder = document.getElementById('uploadPlaceholder');
    if (uploadPlaceholder) {
      this.clickHandler = (e: Event) => {
        e.stopPropagation(); // Prevent event from bubbling
        e.preventDefault(); // Prevent default behavior
        
        // Debounce: prevent double-clicks within 300ms
        const now = Date.now();
        if (now - this.lastClickTime < 300) {
          console.log('[UploadManager] Ignoring rapid click (debounce)');
          return;
        }
        this.lastClickTime = now;
        
        console.log('[UploadManager] Upload placeholder clicked - triggering file input');
        this.fileInput?.click();
      };
      uploadPlaceholder.addEventListener('click', this.clickHandler);
    }

    // File input change
    this.fileInput.addEventListener('change', (e) => {
      const target = e.target as HTMLInputElement;
      const file = target.files?.[0];
      if (file) {
        this.handleFile(file);
      }
    });

    // Drag and drop
    this.uploadZone.addEventListener('dragover', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.uploadZone?.classList.add('drag-over');
    });

    this.uploadZone.addEventListener('dragleave', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.uploadZone?.classList.remove('drag-over');
    });

    this.uploadZone.addEventListener('drop', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.uploadZone?.classList.remove('drag-over');

      const file = e.dataTransfer?.files[0];
      if (file) {
        this.handleFile(file);
      }
    });

    // Prevent default drag behavior on document
    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
      document.addEventListener(eventName, (e) => {
        e.preventDefault();
        e.stopPropagation();
      }, false);
    });
  }

  private handleFile(file: File): void {
    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp', 'image/tiff'];
    
    if (!validTypes.includes(file.type)) {
      alert('Please upload a valid image file (JPG, PNG, GIF, WebP, BMP, or TIFF)');
      return;
    }

    // Validate file size (max 50MB)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      alert('File is too large. Maximum size is 50MB.');
      return;
    }

    // Call the callback
    if (this.onFileUpload) {
      this.onFileUpload(file);
    }
  }

  destroy(): void {
    // Remove event listeners if needed
    this.uploadZone = null;
    this.fileInput = null;
    this.onFileUpload = null;
  }
}
